import { toLoggableEditorError } from '@/components/tiptap/utils/loggable-editor-error';
import { logger } from '@/utils/logger/logger';
import { buildConvertRestToSection, toSafeEditorErrorSummary } from '@heidi/editor/core';
import type { Editor } from '@tiptap/react';

// The pure transaction builder lives in @heidi/editor/core (task 5.2); this
// app-side shell owns dispatch, focus, and logging. Re-exported for existing
// direct importers (the co-located suite).
export { buildConvertRestToSection } from '@heidi/editor/core';

/**
 * Editor-facing wrapper for `buildConvertRestToSection`: dispatches the
 * transaction, or returns `false` so the caller can fall back (e.g. append an
 * empty draft section).
 */
export function convertRestToSectionAtCursor(editor: Editor): boolean {
  const { heading, paragraph, sectionNode } = editor.state.schema.nodes;
  if (!heading || !paragraph || !sectionNode) {
    // Should be unreachable: the button is gated on sections being enabled.
    logger.error('convertRestToSection: section schema nodes are missing');
    return false;
  }
  try {
    const tr = buildConvertRestToSection(editor.state);
    if (!tr) return false;

    editor.view.dispatch(tr.scrollIntoView());
    editor.view.focus();
    return true;
  } catch (error) {
    // Rebuilding sections from document content can hit a shape `createChecked`
    // rejects; fail closed (the caller falls back) rather than crash the editor.
    // Typed safe envelope only (task 4.10): a createChecked rejection can echo
    // document content in its message, which must never reach app logs.
    logger.error('Failed to convert rest of document to section', {
      error: toLoggableEditorError(
        toSafeEditorErrorSummary(error),
        'content_apply_failed'
      ),
    });
    return false;
  }
}
